import {
  needsPortImagesForItinerary,
  resolveItineraryCardImage,
} from '../planmycruisecontainer/imageRotation/imageRotation';
import ItineraryCard from './ItineraryCard';
import {
  CruiseItemDropdown,
  GetShipsResponse,
  IGetShipsResponse,
  ItineraryCardWrapperProps,
} from '@msc-b2c/interfaces/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  fetchContentFragmentsFromCms,
  fetchPortImagesFromFragments,
  getActiveSession,
  GetSlotsMyMSC,
  myAreaSlots,
  replacePlaceholders,
  RootState,
  setShipCF,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';

import { skipToken } from '@reduxjs/toolkit/query';
import {
  CruiseI,
  IFetchShipsFromFragmentsURLParams,
  IGetExperienceDetailsResponse,
} from '@msc-b2c/interfaces/interfaces';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Text } from '@msc-b2c/shared-components';
import { BookingStatusEnum, LoginMode } from '@msc-b2c/interfaces/enums';
import { useNavigate } from 'react-router-dom';
import { getItineraryNameByCanvas, isB2CBooking } from '../utils/myAreaUtils';
import { getRouteToNavigate } from '../navigationmenu/utils/navigationMenuUtils';
import { getCabinByCruiseId, isMulticruise } from '../utils/utils';
import { MulticruiseSelection } from './components/MulticruiseSelection';
import { formatDateRangeForItinerary } from './ItineraryCard.utils';
import dayjs from 'dayjs';

export const ItineraryCardWrapper = ({
  hideInLightLogin,
  pretitle,
  title,
  courtesyHoldTitle,
  viewAllCTALabel,
  youSetSailLabel,
  youSetSailDaysLabel,
  statusLabel,
  bookingNumberLabel,
  itineraryButtonLabel,
  itineraryButtonLink,
  viewBookingDetailsLabel,
  bookingTitleLabel,
  bookingSubtitleLabel,
  dateRangeFormatSameMonthSameYear,
  dateRangeFormatDifferentMonthSameYear,
  dateRangeFormatDifferentYear,
  stateroomLabel,
  stateroomDescription,
  guaranteedStateroomDescription,
  onewayLabel,
  onewayDescription,
  roundtripLabel,
  roundtripDescription,
  visitingLabel,
  visitingDescriptionSeparator,
  balanceDateFormat,
  positiveBalanceDescription,
  negativeBalanceDescription,
  paidBalanceDescription,
  courtesyHoldBalanceDescription,
  courtesyHoldBalanceButtonLabel,
  negativeBalanceLabel,
  paidBalanceLabel,
  positiveBalanceLabel,
  courtesyHoldBalanceLabel,
  dayFieldLabel,
  nightFieldLabel,
  passengerFieldLabel,
  totalRemainingBalanceDescriptionB2B,
  disableRemainingBalanceB2B,
  embarkationLabel,
  embarkationTime,
  negativeBalanceNote,
  multiCruisePlaceholder,
  formatMultiCruiseDropdownPlaceholder,
  itineraryLabel,
  separatorItinerary,
  showMoreItineraryLabel,
  showLessItineraryLabel,
  showMoreItineraryAriaLabel,
  showLessItineraryAriaLabel,
  showMoreItineraryIcon,
  showLessItineraryIcon,
  numberOfVisiblePorts,
  stateroomFormatLabel,
}: ItineraryCardWrapperProps) => {
  // Prepare props
  const props: ItineraryCardWrapperProps = {
    hideInLightLogin,
    pretitle,
    title,
    courtesyHoldTitle,
    viewAllCTALabel,
    youSetSailLabel,
    youSetSailDaysLabel,
    statusLabel,
    balanceDateFormat,
    positiveBalanceDescription,
    negativeBalanceDescription,
    courtesyHoldBalanceDescription,
    courtesyHoldBalanceButtonLabel,
    paidBalanceDescription,
    negativeBalanceLabel,
    paidBalanceLabel,
    positiveBalanceLabel,
    bookingNumberLabel,
    courtesyHoldBalanceLabel,
    itineraryButtonLabel,
    itineraryButtonLink,
    viewBookingDetailsLabel,
    bookingTitleLabel,
    bookingSubtitleLabel,
    dateRangeFormatSameMonthSameYear,
    dateRangeFormatDifferentMonthSameYear,
    dateRangeFormatDifferentYear,
    stateroomLabel,
    stateroomDescription,
    guaranteedStateroomDescription,
    onewayLabel,
    onewayDescription,
    roundtripLabel,
    roundtripDescription,
    visitingLabel,
    visitingDescriptionSeparator,
    dayFieldLabel,
    nightFieldLabel,
    passengerFieldLabel,
    totalRemainingBalanceDescriptionB2B,
    disableRemainingBalanceB2B,
    embarkationLabel,
    embarkationTime,
    negativeBalanceNote,
    multiCruisePlaceholder,
    formatMultiCruiseDropdownPlaceholder,
    itineraryLabel,
    separatorItinerary,
    showMoreItineraryLabel,
    showLessItineraryLabel,
    showMoreItineraryAriaLabel,
    showLessItineraryAriaLabel,
    showMoreItineraryIcon,
    showLessItineraryIcon,
    numberOfVisiblePorts,
    stateroomFormatLabel,
  };

  const dispatch = useDispatch<AppDispatch>();
  const [shipData, setShipData] = useState<GetShipsResponse>();
  const [experiences, setExperiences] = useState<IGetExperienceDetailsResponse>();
  const navigate = useNavigate();

  const { loginMode, bookingNumber = '', cruiseId } = getActiveSession();
  const [multicruise, setMulticruise] = useState<{
    isMulticruise: boolean;
    cruises: CruiseItemDropdown[];
  }>({
    isMulticruise: false,
    cruises: [],
  });

  const { data: bookingData } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });
  const { navigationItems, allMyCruisesLink } = useSelector(
    (state: RootState) => state?.navigationMenuData
  );
  const { itinerary } = useSelector((state: RootState) => state?.myCruise);
  const existingSlots = useSelector((state: RootState) => state?.myCruise?.slots);

  const bookingsWithImages = useSelector(
    (state: RootState) => state.myAreaCommonConfig.bookingsWithImages
  );

  const { ship } = useSelector((state: RootState) => state.bookingContentFragment);
  const portsData = useSelector((state: RootState) => state.itineraryDetails.portsData);

  const itineraryCanvas =
    bookingData?.cabins?.find((cabin) => cabin.cruise.id === cruiseId)?.cruise?.demographicArea ??
    '';
  const areaCode = bookingData?.cabins?.[0]?.cruise?.demographicArea ?? '';
  const genericConfigs = useSelector((state: RootState) => state.genericConfigs);

  const fetchShipDataFromFragments = useCallback(() => {
    if (bookingData && cruiseId) {
      const bookingShipCode =
        getCabinByCruiseId(bookingData?.cabins, cruiseId)?.cruise?.ship?.code ?? '';
      if (bookingShipCode !== ship?.shipCode) {
        fetchContentFragmentsFromCms<IGetShipsResponse, IFetchShipsFromFragmentsURLParams>(
          'getShips',
          {
            shipCode: bookingShipCode,
          }
        ).then((result: IGetShipsResponse) => {
          const selectedShip = result?.data?.shipCfmList?.items?.[0];
          setShipData(selectedShip);

          if (selectedShip) {
            dispatch(setShipCF(selectedShip));
          }
        });
      } else {
        setShipData(ship);
      }

      fetchContentFragmentsFromCms<IGetExperienceDetailsResponse, undefined>('getExperienceDetails')
        .then((result) => {
          setExperiences(result);
        })
        .catch((error) => {
          console.error('Error fetching experience details:', error);
        });
    }
  }, [bookingData, cruiseId, ship]);

  useEffect(() => {
    fetchShipDataFromFragments();
  }, [fetchShipDataFromFragments, cruiseId]);

  useEffect(() => {
    if (bookingData) {
      const cruisesList = isMulticruise(bookingData);

      if (cruisesList.isMulticruise) {
        setMulticruise({
          isMulticruise: cruisesList.isMulticruise,
          cruises:
            cruisesList.cruises.map((cruise: CruiseI) => {
              return {
                value: cruise.id,
                text:
                  replacePlaceholders(formatMultiCruiseDropdownPlaceholder ?? '', {
                    shipName: cruise.ship.name,
                    date: formatDateRangeForItinerary(
                      dayjs(cruise.departureDate).toDate(),
                      dayjs(cruise.arrivalDate).toDate(),
                      {
                        sameMonthSameYear: dateRangeFormatSameMonthSameYear,
                        differentMonthSameYear: dateRangeFormatDifferentMonthSameYear,
                        differentYear: dateRangeFormatDifferentYear,
                      }
                    ),
                  }) ?? '',
              };
            }) || [],
        });
      }

      const cabinId = bookingData.passengers?.[0]?.checkIn?.[0]?.cabinId;
      // Guard against the logout race: when the user clicks logout, this
      // effect can re-fire briefly with bookingNumber='' before bookingData
      // is cleared by the post-logout re-render cascade. Skip the slots call
      // unless we still have both required path parameters.
      if (bookingNumber && cabinId && !existingSlots) {
        const getSlotsData = new GetSlotsMyMSC(false, {
          bookingNumber: Number(bookingNumber),
          cabinId,
        });
        getSlotsData.getData().then((res) => {
          dispatch(myAreaSlots(res));
        });
      }
    }
  }, [
    bookingData,
    bookingNumber,
    dateRangeFormatDifferentMonthSameYear,
    dateRangeFormatDifferentYear,
    dateRangeFormatSameMonthSameYear,
    dispatch,
    formatMultiCruiseDropdownPlaceholder,
  ]);

  useEffect(() => {
    if (needsPortImagesForItinerary(itinerary, genericConfigs) && portsData === null) {
      fetchPortImagesFromFragments(dispatch);
    }
  }, [dispatch, itinerary, genericConfigs, portsData]);

  const needsPortImages = useMemo(
    () => needsPortImagesForItinerary(itinerary, genericConfigs),
    [itinerary, genericConfigs]
  );
  const isPortImagesLoading = needsPortImages && portsData === null;

  const itinImage = useMemo(() => {
    if (!itinerary || isPortImagesLoading) return undefined;
    return (
      resolveItineraryCardImage(itinerary, areaCode, genericConfigs, portsData) ??
      bookingsWithImages.find((b: any) => b.bookingNumber === bookingNumber)?.image
    );
  }, [
    itinerary,
    areaCode,
    genericConfigs,
    portsData,
    isPortImagesLoading,
    bookingsWithImages,
    bookingNumber,
  ]);

  const isCardReady = !!(bookingData && itinerary && shipData && !isPortImagesLoading);

  const handleViewAllNavigate = () => {
    if (navigationItems?.length !== 0) {
      const route = getRouteToNavigate(navigationItems || [], allMyCruisesLink?.url ?? '');
      navigate(`${route}${globalThis.location.href.includes('.html') ? '.html' : ''}`);
    }
  };

  useEffect(() => {
    const skeletonElements = [
      '.cmp-itineraryCard-skeleton__pretitle',
      '.cmp-itineraryCard-skeleton__title',
      '.cmp-itineraryCard-skeleton__view-all',
      '.cmp-itineraryCard-container-skeleton',
    ].map((selector) => document.querySelector(selector));

    skeletonElements.forEach((element) => {
      element?.classList.toggle('hide', isCardReady);
    });
  }, [isCardReady]);

  if (loginMode === LoginMode.LIGHT && hideInLightLogin) {
    return null;
  }

  // Pass the fetched data to ItineraryCard as props
  return (
    isCardReady && (
      <div className={'cmp-itineraryCard-wrapper'}>
        {bookingNumber && cruiseId && bookingData && multicruise.isMulticruise && (
          <div className={'cmp-itineraryCard-wrapper__multicruise-selection'}>
            <MulticruiseSelection
              defaultValue={cruiseId}
              multicruise={multicruise}
              multiCruisePlaceholder={multiCruisePlaceholder}
            />
          </div>
        )}

        <Text
          text={pretitle}
          styleOption={'cmp-itineraryCard-wrapper__pretitle body-big spacer-md-mb-024'}
        />
        <div className="cmp-itineraryCard-wrapper__header">
          <Text
            text={
              bookingData?.status.code === BookingStatusEnum.OPTIONAL && isB2CBooking(bookingData)
                ? courtesyHoldTitle
                : title
            }
            styleOption={'cmp-itineraryCard-wrapper__title header-m'}
          />

          {loginMode === LoginMode.FULL && (
            <Button
              text={viewAllCTALabel}
              onClick={() => handleViewAllNavigate()}
              icon={'right'}
              styleOption={
                'cmp-button__link -position-right view-all-button view-all-button-desktop'
              }
            />
          )}
        </div>
        {bookingData &&
          itinerary &&
          shipData &&
          (loginMode === LoginMode.FULL ||
            (loginMode === LoginMode.LIGHT && !hideInLightLogin)) && (
            <ItineraryCard
              itineraryLabels={props}
              bookingData={bookingData}
              canvasItineraryName={getItineraryNameByCanvas(itineraryCanvas)}
              itineraryData={itinerary}
              shipData={shipData}
              imageData={itinImage}
              isAllMyCruises={false}
              experiences={experiences}
              disableRemainingBalanceB2B={disableRemainingBalanceB2B}
            />
          )}

        {loginMode === LoginMode.FULL && (
          <Button
            text={viewAllCTALabel}
            onClick={() => handleViewAllNavigate()}
            icon={'right'}
            styleOption={'cmp-button__link -position-right view-all-button'}
          />
        )}
      </div>
    )
  );
};

ItineraryCardWrapper.aemResourceType = 'b2c-website-foundation/components/mymsc/itinerarycard';

export default ItineraryCardWrapper;
